'use client';
import type { CurrentUserDetails } from '@unifyapps/network/generated/models/currentUserDetails';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { create } from 'zustand';
import { createSelectors } from '../utils/state/zustand';
import type { UserRuntimeEnvDetails } from './nocode/types';

export type UserDetails = CurrentUserDetails & { external?: boolean };

type Actions = {
  /**
   * used to set user details fetched from backend in userStore.
   */
  setCurrentUserDetails: (currentUserDetails: UserDetails | undefined) => void;

  /**
   * used to set the user runtime environment details(e.g device details, network details, etc.) in userStore.
   * This details are not fetched from backend.
   * This details are used in NoCode for initialising the user context in GlobalAppStateStore.
   */
  setCurrentUserRuntimeEnvDetails: (currentUserExtraDetails: UserRuntimeEnvDetails) => void;

  /**
   *  clear complete user store
   */
  clearUserStore: () => void;

  /**
   *  clear only user details fetched from backend and not the user runtime environment details
   */
  clearCurrentUserDetails: () => void;
};

const createUserStore = (
  initialUserDetails?: UserDetails,
  initialUserRuntimeEnvDetails?: UserRuntimeEnvDetails,
) => {
  return createSelectors(
    create<{
      currentUserDetails: UserDetails | undefined;
      currentUserRuntimeEnvDetails: UserRuntimeEnvDetails;
      isLoggedIn: boolean;
      actions: Actions;
    }>()((set) => {
      return {
        currentUserDetails: initialUserDetails,
        currentUserRuntimeEnvDetails: initialUserRuntimeEnvDetails,
        isLoggedIn: Boolean(initialUserDetails?.user?.id),
        actions: {
          setCurrentUserDetails: (currentUserDetails: UserDetails | undefined) => {
            set({ currentUserDetails, isLoggedIn: Boolean(currentUserDetails?.user?.id) });
          },
          clearUserStore: () => {
            set({
              currentUserDetails: undefined,
              currentUserRuntimeEnvDetails: undefined,
              isLoggedIn: false,
            });
          },
          clearCurrentUserDetails: () => {
            set({ currentUserDetails: undefined, isLoggedIn: false });
          },
          setCurrentUserRuntimeEnvDetails: (
            currentUserRuntimeEnvDetails: UserRuntimeEnvDetails,
          ) => {
            set({ currentUserRuntimeEnvDetails });
          },
        },
      };
    }),
  );
};

type UserStore = ReturnType<typeof createUserStore>;

const UserStoreContext = createContext<UserStore | null>(null);

export function useUserStore(): UserStore {
  const context = useContext(UserStoreContext);

  if (!context) {
    throw new Error('[useUserStore] Must be used within UserStoreProvider');
  }

  return context;
}

interface UserStoreProviderProps {
  children: ReactNode;
  /**
   * Initial details of the user, that are fetched from backend.
   */
  initialUserDetails?: UserDetails;

  /**
   * Contains the user runtime environment details(e.g device details, network details, etc.) that are not fetched from backend.
   */
  initialUserRuntimeEnvDetails?: UserRuntimeEnvDetails;
}

export function UserStoreProvider({
  children,
  initialUserDetails,
  initialUserRuntimeEnvDetails,
}: UserStoreProviderProps) {
  const [store] = useState(() => createUserStore(initialUserDetails, initialUserRuntimeEnvDetails));

  return <UserStoreContext.Provider value={store}>{children}</UserStoreContext.Provider>;
}
